import { apiFetch } from "@hub/ui/api";
import { relativeTime } from "@hub/ui/format";
import { useEffect, useState } from "react";

export interface ReconcileReport {
  ranAt: string;
  rewroteDns: string[];
  orphans: { instanceId: string; slug: string }[];
  missing: string[];
  stranded: string[];
}

/**
 * True when the last sweep found something an operator should look at.
 * Exported and tested directly -- this repo has no jsdom/happy-dom, no
 * @testing-library/react (see ProvisioningProgress's own note on this), so
 * component logic worth a regression test is pulled out as a plain function
 * rather than rendered.
 */
export function hasDrift(report: ReconcileReport | null): boolean {
  if (!report) return false;
  return report.orphans.length > 0 || report.missing.length > 0 || report.stranded.length > 0;
}

/**
 * Surfaces the hourly reconciler's drift report on the fleet page. Before
 * this, `console.warn` (reconciler.ts) was the ONLY sink -- nobody tails
 * `docker logs` on a running container at 2am (review finding 4). Orphans
 * are money and `missing` is an outage; this is that alarm channel actually
 * getting a reader.
 *
 * Silent on a clean sweep or before the first sweep has ever run, same as
 * the reconciler's own `console.warn` -- a banner that is always present
 * (even saying "all clear") trains an operator to stop reading it.
 */
export function DriftBanner() {
  const [report, setReport] = useState<ReconcileReport | null>(null);

  useEffect(() => {
    apiFetch<ReconcileReport | null>("/api/fleet/reconcile-report")
      .then(setReport)
      .catch(() => {
        // Same fail-open as FleetStats: a banner that cannot load is not
        // worth blocking the rest of the page over. The reconciler's own
        // console.warn is still there as the fallback sink.
      });
  }, []);

  if (!hasDrift(report)) return null;
  const r = report as ReconcileReport;

  return (
    <div
      role="alert"
      className="mb-6 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
    >
      <p className="font-medium">
        The last reconcile sweep ({relativeTime(r.ranAt)}) found drift that needs a look.
      </p>
      <ul className="mt-1.5 list-inside list-disc space-y-0.5">
        {r.orphans.length > 0 ? (
          <li>
            {r.orphans.length} orphan{r.orphans.length === 1 ? "" : "s"} — live and billing, no row:{" "}
            {r.orphans.map((o) => `${o.slug} (${o.instanceId})`).join(", ")}
          </li>
        ) : null}
        {r.missing.length > 0 ? (
          <li>
            {r.missing.length} missing — a row believes it has a running instance AWS no longer has:{" "}
            {r.missing.join(", ")}
          </li>
        ) : null}
        {r.stranded.length > 0 ? (
          <li>
            {r.stranded.length} stranded — a failed row still holds a live, billing instance:{" "}
            {r.stranded.join(", ")}
          </li>
        ) : null}
      </ul>
    </div>
  );
}
